/* Map bootstrap. Registers the two custom protocols and builds the style.
 *
 * This lives in shared, alongside map-style.ts, for the reason Addendum A2
 * gives: anything that appears on both surfaces lives here or it drifts. The
 * desk having its own map setup is exactly how the two maps stopped matching
 * the first time — the phone got terrain and contours, the desk got flat sand.
 *
 * NOT exported from the package barrel. It pulls in maplibre-gl and
 * maplibre-contour, and a capture screen with no map on it should not carry
 * a megabyte of mapping code. Import from '@kilroy/shared/map'.
 *
 *   pmtiles://   an offline property pack in OPFS, when one has been downloaded
 *   <dem>://     contour lines computed in a worker from elevation tiles
 *
 * Both are registered once per document. Registering twice throws, and in dev
 * StrictMode mounts everything twice, so the guard is not optional.
 */

import * as maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import mlcontour from 'maplibre-contour';
import { DEM_TILES, kilroyStyle } from './map-style';

/* MapLibre finds its parser worker with
     new URL('./maplibre-gl-worker.mjs', import.meta.url)
   which is correct in node_modules and wrong after bundling: import.meta.url
   becomes the app chunk, and no worker file sits beside it.
   
   It fails in the worst possible way. Tiles keep being fetched — that happens
   on the main thread — so the network tab looks perfectly healthy while
   nothing is ever decoded. No console error. The map renders its background
   colour and stops. It cost an afternoon.
   
   ?worker&url — NOT plain ?url. The distinction is the whole bug: ?url copies
   that ONE file as an asset, but the worker relatively imports
   maplibre-gl-shared.mjs, which never gets emitted. The worker then dies on
   its first import, silently, and you are back to a map that fetches tiles
   and paints nothing. ?worker&url bundles the worker WITH its dependencies.

   Verified against a 30-line bare-MapLibre repro on a default Vite config:
   dev styleLoaded=true, prod styleLoaded=false, and prod=true after this
   change. It was never our style, our contours, or our CSS. */
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';

maplibregl.setWorkerUrl(workerUrl);

type DemSource = InstanceType<typeof mlcontour.DemSource>;
let demSource: DemSource | null = null;
let registered = false;

export function setupMap(): { contourUrl: string } {
  if (!registered) {
    maplibregl.addProtocol('pmtiles', new Protocol().tile);

    demSource = new mlcontour.DemSource({
      url: DEM_TILES,
      encoding: 'terrarium',
      maxzoom: 13,
      // Off the main thread: contour generation is real work and the map has
      // to stay responsive to a thumb while it happens.
      worker: true,
    });
    demSource.setupMaplibre(maplibregl);
    registered = true;
  }

  return {
    contourUrl: demSource!.contourProtocolUrl({
      // Metric, and interval by zoom: 100 m index / 20 m minor when zoomed out,
      // tightening to 50 / 10 on the outcrop. A fixed interval is either
      // useless at property scale or unreadable at claim scale.
      thresholds: {
        11: [100, 500],
        12: [50, 250],
        13: [20, 100],
        14: [20, 100],
        15: [10, 50],
      },
      elevationKey: 'ele',
      levelKey: 'level',
      contourLayer: 'contours',
    }),
  };
}

/** The style, with contours wired in. */
export function buildStyle(online: boolean) {
  const { contourUrl } = setupMap();
  return kilroyStyle({ online, contourUrl }) as maplibregl.StyleSpecification;
}

export { maplibregl };
